import { FormEvent, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ActivityEvent,
  ActivityFacets,
  ActivityPage,
  api,
} from "../lib/api";
import { useAuth } from "../lib/auth";

interface Filters {
  q: string;
  event_type: string;
  event_prefix: string;
  actor_user_id: number | null;
  case_id: number | null;
  date_from: string;
  date_to: string;
  limit: number;
  offset: number;
}

const DEFAULT_FILTERS: Filters = {
  q: "",
  event_type: "",
  event_prefix: "",
  actor_user_id: null,
  case_id: null,
  date_from: "",
  date_to: "",
  limit: 50,
  offset: 0,
};

// Coarse event-type buckets that map onto the dotted prefixes used by the
// backend (e.g. "auth.login.success" -> "auth"). Lets users say "show me all
// document activity" without hunting through 30 individual event types.
const PREFIX_OPTIONS: { value: string; label: string }[] = [
  { value: "",             label: "Any category" },
  { value: "auth",         label: "Auth & login" },
  { value: "case",         label: "Cases" },
  { value: "document",     label: "Documents" },
  { value: "conversation", label: "Chats" },
  { value: "query",        label: "Questions asked" },
  { value: "note",         label: "Notes" },
  { value: "user",         label: "User admin" },
];

export default function Activity() {
  const user = useAuth((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState<ActivityPage | null>(null);
  const [facets, setFacets] = useState<ActivityFacets | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<ActivityEvent | null>(null);

  // Filters live in the URL so a deep-linked Activity view (e.g. "all logins
  // by Alice this week") is shareable / refresh-safe.
  const filters: Filters = useMemo(() => readFiltersFromUrl(searchParams), [searchParams]);
  const [draftQ, setDraftQ] = useState(filters.q);
  useEffect(() => { setDraftQ(filters.q); }, [filters.q]);

  // Close the details modal on Escape.
  useEffect(() => {
    if (!selected) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setSelected(null);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selected]);

  // Load filter facets once. The org's distinct events / actors / cases
  // populate the dropdowns so a user only sees options that have hits.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const f = await api.get<ActivityFacets>("/activity/facets");
        if (!cancelled) setFacets(f);
      } catch (e: any) {
        if (!cancelled) setErr(e?.message ?? "Failed to load filters");
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Re-fetch the page whenever any filter / pagination param changes.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBusy(true);
      setErr(null);
      try {
        const p = new URLSearchParams();
        if (filters.q.trim()) p.set("q", filters.q.trim());
        if (filters.event_type) p.set("event_type", filters.event_type);
        else if (filters.event_prefix) p.set("event_prefix", filters.event_prefix);
        if (filters.actor_user_id != null) p.set("actor_user_id", String(filters.actor_user_id));
        if (filters.case_id != null) p.set("case_id", String(filters.case_id));
        if (filters.date_from) p.set("date_from", filters.date_from);
        if (filters.date_to) p.set("date_to", filters.date_to);
        p.set("limit", String(filters.limit));
        p.set("offset", String(filters.offset));
        const data = await api.get<ActivityPage>(`/activity?${p.toString()}`);
        if (!cancelled) {
          setPage(data);
        }
      } catch (e: any) {
        if (!cancelled) {
          setPage(null);
          setErr(e?.message ?? "Failed to load activity");
        }
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [filters]);

  function update(patch: Partial<Filters>) {
    // Any filter change resets pagination — otherwise we'd silently drop the
    // user onto e.g. page 5 of a smaller result set.
    const next: Filters = { ...filters, ...patch };
    if (!("offset" in patch)) next.offset = 0;
    // Picking a specific event type is more specific than a prefix, so
    // mutually exclude them in the URL.
    if (patch.event_type) next.event_prefix = "";
    if (patch.event_prefix) next.event_type = "";
    setSearchParams(filtersToUrl(next), { replace: true });
  }

  function resetFilters() {
    setSearchParams(new URLSearchParams(), { replace: true });
  }

  function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    update({ q: draftQ });
  }

  if (user && user.role !== "owner" && user.role !== "admin") {
    return (
      <div className="activity">
        <header className="activity__head">
          <div>
            <div className="page-eyebrow">Activity log</div>
            <h1>Activity log</h1>
          </div>
          <Link to="/" className="btn">← Back to cases</Link>
        </header>
        <div className="alert alert--error" role="alert">
          The activity log is restricted to organization owners and admins.
        </div>
      </div>
    );
  }

  const total = page?.total ?? 0;
  const pageStart = total === 0 ? 0 : filters.offset + 1;
  const pageEnd = Math.min(filters.offset + filters.limit, total);
  const hasPrev = filters.offset > 0;
  const hasNext = filters.offset + filters.limit < total;

  return (
    <div className="activity">
      <header className="activity__head">
        <div>
          <div className="page-eyebrow">Activity log</div>
          <h1>Activity log</h1>
          <p className="activity__lead">
            Every login, case access, document view, chat message, and note
            change in your organization. Scoped to your org only.
          </p>
        </div>
        <Link to="/" className="btn">← Back to cases</Link>
      </header>

      {err && <div className="alert alert--error">{err}</div>}

      <form className="activity__filters" onSubmit={onSubmit}>
        <div className="activity__filter activity__filter--wide">
          <label htmlFor="ac-q">Search</label>
          <input
            id="ac-q"
            type="search"
            placeholder="User, event, case, or target…"
            value={draftQ}
            onChange={(e) => setDraftQ(e.target.value)}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-cat">Category</label>
          <select
            id="ac-cat"
            value={filters.event_prefix}
            onChange={(e) => update({ event_prefix: e.target.value })}
            disabled={!!filters.event_type}
            title={filters.event_type ? "Clear the specific event type to use a category" : undefined}
          >
            {PREFIX_OPTIONS.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-event">Event type</label>
          <select
            id="ac-event"
            value={filters.event_type}
            onChange={(e) => update({ event_type: e.target.value })}
          >
            <option value="">Any event</option>
            {(facets?.event_types ?? []).map((e) => (
              <option key={e.event_type} value={e.event_type}>
                {e.event_label} ({e.count})
              </option>
            ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-user">User</label>
          <select
            id="ac-user"
            value={filters.actor_user_id ?? ""}
            onChange={(e) => update({ actor_user_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Anyone</option>
            {(facets?.actors ?? [])
              .filter((a) => a.user_id != null)
              .map((a) => (
                <option key={String(a.user_id)} value={String(a.user_id)}>
                  {a.display_name ?? a.email ?? `User #${a.user_id}`} ({a.count})
                </option>
              ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-case">Case</label>
          <select
            id="ac-case"
            value={filters.case_id ?? ""}
            onChange={(e) => update({ case_id: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">Any case</option>
            {(facets?.cases ?? []).map((c) => (
              <option key={c.case_id} value={c.case_id}>
                {c.case_name ?? `Case #${c.case_id}`} ({c.count})
              </option>
            ))}
          </select>
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-from">From</label>
          <input
            id="ac-from"
            type="date"
            value={filters.date_from}
            onChange={(e) => update({ date_from: e.target.value })}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-to">To</label>
          <input
            id="ac-to"
            type="date"
            value={filters.date_to}
            onChange={(e) => update({ date_to: e.target.value })}
          />
        </div>
        <div className="activity__filter">
          <label htmlFor="ac-limit">Page size</label>
          <select
            id="ac-limit"
            value={filters.limit}
            onChange={(e) => update({ limit: Number(e.target.value) })}
          >
            {[25, 50, 100, 200].map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </div>
        <div className="activity__filter activity__filter--actions">
          <button type="submit" className="btn btn--primary">Search</button>
          <button type="button" className="btn" onClick={resetFilters}>Reset</button>
        </div>
      </form>

      <div className="activity__meta muted">
        {busy && <span>Loading…</span>}
        {!busy && page && (
          <span>
            {total === 0
              ? "No activity matches these filters."
              : `Showing ${pageStart.toLocaleString()}–${pageEnd.toLocaleString()} of ${total.toLocaleString()}`}
          </span>
        )}
      </div>

      <div className="activity__scroll">
        <table className="activity__table">
          <thead>
            <tr>
              <th className="activity__col-time">When</th>
              <th className="activity__col-event">Event</th>
              <th className="activity__col-actor">User</th>
              <th className="activity__col-case">Case</th>
              <th className="activity__col-target">Target</th>
              <th className="activity__col-ip">IP</th>
            </tr>
          </thead>
          <tbody>
            {(page?.items ?? []).map((row) => (
              <tr
                key={row.id}
                className={`activity__row activity__row--${categoryFor(row.event_type)} activity__row--clickable`}
                tabIndex={0}
                role="button"
                aria-label={`Show details for ${row.event_label}`}
                onClick={() => setSelected(row)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setSelected(row);
                  }
                }}
              >
                <td className="activity__col-time" title={row.ts}>
                  {formatTime(row.ts)}
                </td>
                <td className="activity__col-event">
                  <span className={`activity__chip activity__chip--${categoryFor(row.event_type)}`}>
                    {row.event_label}
                  </span>
                  <div className="activity__event-id muted">{row.event_type}</div>
                </td>
                <td className="activity__col-actor">
                  {row.actor_display_name ? (
                    <>
                      <div>{row.actor_display_name}</div>
                      {row.actor_email && (
                        <div className="muted activity__sub">{row.actor_email}</div>
                      )}
                    </>
                  ) : row.actor_email ? (
                    <span>{row.actor_email}</span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="activity__col-case">
                  {row.case_id ? (
                    <Link
                      to={`/cases/${row.case_id}`}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {row.case_name ?? `Case #${row.case_id}`}
                    </Link>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="activity__col-target">
                  {row.target_label ? (
                    <span title={row.target_type ?? undefined}>
                      {row.target_label}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="activity__col-ip">
                  <span className="muted">{row.ip_address ?? "—"}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {page && page.items.length === 0 && !busy && (
          <div className="empty">
            No activity rows match these filters. Try widening the date range
            or clearing the user / case filter.
          </div>
        )}
      </div>

      <div className="activity__pager">
        <button
          type="button"
          className="btn"
          disabled={!hasPrev || busy}
          onClick={() => update({ offset: Math.max(0, filters.offset - filters.limit) })}
        >
          ← Previous
        </button>
        <span className="muted">
          Page {Math.floor(filters.offset / filters.limit) + 1}
          {total > 0 && ` of ${Math.max(1, Math.ceil(total / filters.limit))}`}
        </span>
        <button
          type="button"
          className="btn"
          disabled={!hasNext || busy}
          onClick={() => update({ offset: filters.offset + filters.limit })}
        >
          Next →
        </button>
      </div>

      {selected && (
        <ActivityDetailModal event={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  );
}

function ActivityDetailModal({
  event,
  onClose,
}: {
  event: ActivityEvent;
  onClose: () => void;
}) {
  const cat = categoryFor(event.event_type);
  return (
    <div
      className="activity-modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="activity-modal" role="dialog" aria-modal="true" aria-labelledby="ev-title">
        <header className="activity-modal__head">
          <div>
            <span className={`activity__chip activity__chip--${cat}`}>{event.event_label}</span>
            <h2 id="ev-title" className="activity-modal__title">
              {event.target_label
                ? `${event.event_label} — ${event.target_label}`
                : event.event_label}
            </h2>
            <div className="activity__event-id muted">{event.event_type}</div>
          </div>
          <button
            type="button"
            className="activity-modal__close"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <dl className="activity-modal__grid">
          <dt>When</dt>
          <dd>
            {formatTime(event.ts)}
            <div className="muted activity__sub">{event.ts}</div>
          </dd>

          <dt>User</dt>
          <dd>
            {event.actor_display_name || event.actor_email ? (
              <>
                {event.actor_display_name && <div>{event.actor_display_name}</div>}
                {event.actor_email && (
                  <div className="muted activity__sub">{event.actor_email}</div>
                )}
                {event.actor_user_id && (
                  <div className="muted activity__sub">User #{event.actor_user_id}</div>
                )}
              </>
            ) : (
              <span className="muted">—</span>
            )}
          </dd>

          <dt>Case</dt>
          <dd>
            {event.case_id ? (
              <Link to={`/cases/${event.case_id}`} onClick={onClose}>
                {event.case_name ?? `Case #${event.case_id}`}
              </Link>
            ) : (
              <span className="muted">—</span>
            )}
          </dd>

          <dt>Target</dt>
          <dd>
            {event.target_label || event.target_type || event.target_id ? (
              <>
                {event.target_label && <div>{event.target_label}</div>}
                {(event.target_type || event.target_id) && (
                  <div className="muted activity__sub">
                    {event.target_type ?? "?"}
                    {event.target_id != null ? ` #${event.target_id}` : ""}
                  </div>
                )}
              </>
            ) : (
              <span className="muted">—</span>
            )}
          </dd>

          <dt>IP address</dt>
          <dd className="activity-modal__mono">{event.ip_address ?? <span className="muted">—</span>}</dd>

          <dt>User agent</dt>
          <dd className="activity-modal__mono activity-modal__wrap">
            {event.user_agent ?? <span className="muted">—</span>}
          </dd>

          {event.metadata && Object.keys(event.metadata).length > 0 && (
            <>
              <dt>Metadata</dt>
              <dd>
                <pre className="activity-modal__json">
                  {JSON.stringify(event.metadata, null, 2)}
                </pre>
              </dd>
            </>
          )}
        </dl>

        <footer className="activity-modal__foot">
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </footer>
      </div>
    </div>
  );
}

function categoryFor(eventType: string): string {
  return eventType.split(".")[0] ?? "other";
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function readFiltersFromUrl(p: URLSearchParams): Filters {
  const limit = clamp(Number(p.get("limit") ?? DEFAULT_FILTERS.limit) || DEFAULT_FILTERS.limit, 1, 200);
  const offset = Math.max(0, Number(p.get("offset") ?? 0) || 0);
  return {
    q: p.get("q") ?? "",
    event_type: p.get("event_type") ?? "",
    event_prefix: p.get("event_prefix") ?? "",
    actor_user_id: p.get("actor_user_id") ? Number(p.get("actor_user_id")) : null,
    case_id: p.get("case_id") ? Number(p.get("case_id")) : null,
    date_from: p.get("date_from") ?? "",
    date_to: p.get("date_to") ?? "",
    limit,
    offset,
  };
}

function filtersToUrl(f: Filters): URLSearchParams {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.event_type) p.set("event_type", f.event_type);
  else if (f.event_prefix) p.set("event_prefix", f.event_prefix);
  if (f.actor_user_id != null) p.set("actor_user_id", String(f.actor_user_id));
  if (f.case_id != null) p.set("case_id", String(f.case_id));
  if (f.date_from) p.set("date_from", f.date_from);
  if (f.date_to) p.set("date_to", f.date_to);
  if (f.limit !== DEFAULT_FILTERS.limit) p.set("limit", String(f.limit));
  if (f.offset > 0) p.set("offset", String(f.offset));
  return p;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, n));
}
